import { useCallback, useEffect, useState } from "react";
import { adminApi, ApiRequestError, type AdminPlan, type AdminPrincipal, type AuditLog, type ClientRelease, type Customer, type DashboardData, type Entitlement, type NodeItem, type Order, type RoutePolicy, type SystemSetting } from "../lib/api";

export type AdminDataState = {
  status: "loading" | "unauthenticated" | "ready" | "error";
  admin: AdminPrincipal | null;
  dashboard: DashboardData | null;
  customers: Customer[];
  orders: Order[];
  plans: AdminPlan[];
  entitlements: Entitlement[];
  nodes: NodeItem[];
  routes: RoutePolicy[];
  releases: ClientRelease[];
  settings: SystemSetting[];
  audit: AuditLog[];
  error: string | null;
  refresh: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  mutate: (action: () => Promise<unknown>) => Promise<void>;
};

type AdminCollections = Pick<AdminDataState, "dashboard" | "customers" | "orders" | "plans" | "entitlements" | "nodes" | "routes" | "releases" | "settings" | "audit">;

const emptyData: AdminCollections = {
  dashboard: null, customers: [], orders: [], plans: [], entitlements: [], nodes: [], routes: [], releases: [], settings: [], audit: [],
};

export function useAdminData(): AdminDataState {
  const [status, setStatus] = useState<AdminDataState["status"]>("loading");
  const [admin, setAdmin] = useState<AdminPrincipal | null>(null);
  const [data, setData] = useState(emptyData);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setStatus("loading"); setError(null);
    try {
      const principal = await adminApi.session();
      setAdmin(principal);
      const [dashboard, customers, orders, plans, entitlements, nodes, routes, releases, settings, audit] = await Promise.all([
        adminApi.dashboard(), adminApi.customers(), adminApi.orders(), adminApi.plans(), adminApi.entitlements(),
        adminApi.nodes(), adminApi.routes(), adminApi.releases(), adminApi.settings(), adminApi.audit(),
      ]);
      setData({ dashboard, customers, orders, plans, entitlements, nodes, routes, releases, settings, audit });
      setStatus("ready");
    } catch (caught) {
      if (caught instanceof ApiRequestError && caught.status === 401) {
        setAdmin(null); setData(emptyData); setStatus("unauthenticated"); return;
      }
      setError(caught instanceof Error ? caught.message : "无法连接管理 API"); setStatus("error");
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const login = useCallback(async (email: string, password: string) => {
    setError(null);
    try { await adminApi.login(email, password); await load(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "登录失败"); throw caught; }
  }, [load]);

  const logout = useCallback(async () => {
    await adminApi.logout(); setAdmin(null); setData(emptyData); setStatus("unauthenticated");
  }, []);

  const mutate = useCallback(async (action: () => Promise<unknown>) => {
    setError(null);
    try { await action(); await load(); }
    catch (caught) { setError(caught instanceof Error ? caught.message : "操作失败"); throw caught; }
  }, [load]);

  return { status, admin, ...data, error, refresh: load, login, logout, mutate };
}
